import React, { useEffect, useMemo, useRef, useState } from 'react';
import { collection, onSnapshot, orderBy, query } from 'firebase/firestore';
import { db } from '../firebase';
import type { Comment as CommentType, User, Zip } from '../types';
import PaperAirplaneIcon from './icons/PaperAirplaneIcon';
import XMarkIcon from './icons/XMarkIcon';
import OvalDotsIcon from './icons/OvalDotsIcon';

interface ZipCommentsPageProps {
  zip: Zip;
  currentUser: User | null;
  allUsers: User[];
  onClose: () => void;
  onPromptLogin: () => void;
  onViewProfile: (user: User) => void;
  onToggleLike: (zipId: string) => void;
  onAddComment: (zipId: string, text: string, parentId?: string | null) => void | Promise<void>;
  onDeleteComment?: (zipId: string, commentId: string) => void;
  onReportComment?: (zipId: string, commentId: string) => void;
  onReportZip?: (zipId: string) => void;
}

type ThreadedComment = CommentType & {
  children: ThreadedComment[];
};

// Instagram/TikTok-style comment sheet: slides up over the still-playing zip
// video (which stays mounted behind the scrim) instead of navigating to a
// separate full-page route.
const ZipCommentsPage: React.FC<ZipCommentsPageProps> = ({
  zip,
  currentUser,
  allUsers,
  onClose,
  onPromptLogin,
  onViewProfile,
  onAddComment,
  onDeleteComment,
  onReportComment,
  onReportZip,
}) => {
  const isModerator = currentUser?.username?.toLowerCase() === 'ridaz';
  const [comments, setComments] = useState<CommentType[]>([]);
  const [isOptionsMenuOpen, setIsOptionsMenuOpen] = useState(false);
  const [newComment, setNewComment] = useState('');
  const [mentionQuery, setMentionQuery] = useState('');
  const [isPostingComment, setIsPostingComment] = useState(false);
  const [replyTarget, setReplyTarget] = useState<CommentType | null>(null);
  const [slideIn, setSlideIn] = useState(false);
  const composerInputRef = useRef<HTMLInputElement | null>(null);

  const handleRequestClose = () => {
    setSlideIn(false);
    setTimeout(onClose, 200);
  };

  useEffect(() => {
    requestAnimationFrame(() => setSlideIn(true));
    const original = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = original;
    };
  }, []);

  useEffect(() => {
    const commentsRef = collection(db, 'zips', zip.id, 'comments');
    const commentsQuery = query(commentsRef, orderBy('timestamp', 'asc'));
    const unsubscribe = onSnapshot(commentsQuery, (snapshot) => {
      setComments(snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() } as CommentType)));
    });
    return () => unsubscribe();
  }, [zip.id]);

  const mentionLookup = useMemo(() => {
    const map: Record<string, User> = {};
    allUsers.forEach((user) => {
      map[user.username.toLowerCase()] = user;
    });
    return map;
  }, [allUsers]);

  const threadedComments = useMemo<ThreadedComment[]>(() => {
    const nodes = new Map<string, ThreadedComment>();
    comments.forEach((comment) => {
      nodes.set(comment.id, { ...comment, children: [] });
    });

    const roots: ThreadedComment[] = [];
    comments.forEach((comment) => {
      const node = nodes.get(comment.id);
      if (!node) return;
      const parentId = comment.parentId || null;
      if (parentId && nodes.has(parentId)) {
        nodes.get(parentId)?.children.push(node);
      } else {
        roots.push(node);
      }
    });

    return roots;
  }, [comments]);

  const renderWithMentions = (text: string) => {
    return text.split(/(@[A-Za-z0-9_]+)/g).map((part, idx) => {
      if (!part.startsWith('@')) return <span key={idx}>{part}</span>;
      const username = part.slice(1).toLowerCase();
      const target = mentionLookup[username];
      if (!target) return <span key={idx} className="text-lime-300">{part}</span>;
      return (
        <button
          key={idx}
          className="text-lime-300 hover:underline"
          onClick={() => { onClose(); onViewProfile(target); }}
        >
          {part}
        </button>
      );
    });
  };

  const formatTimeAgo = (value: unknown) => {
    let timestampMs = 0;
    if (typeof value === 'number') {
      timestampMs = value;
    } else if (typeof value === 'string') {
      const parsed = new Date(value).getTime();
      timestampMs = Number.isFinite(parsed) ? parsed : 0;
    } else if (
      value &&
      typeof value === 'object' &&
      'toDate' in value &&
      typeof (value as { toDate: () => Date }).toDate === 'function'
    ) {
      timestampMs = (value as { toDate: () => Date }).toDate().getTime();
    }
    if (!timestampMs) return 'Just now';
    const diff = Math.max(0, Date.now() - timestampMs);
    const sec = Math.floor(diff / 1000);
    if (sec < 45) return 'Just now';
    const min = Math.floor(sec / 60);
    if (min < 60) return `${min}m ago`;
    const hr = Math.floor(min / 60);
    if (hr < 24) return `${hr}h ago`;
    const day = Math.floor(hr / 24);
    if (day < 7) return `${day}d ago`;
    const week = Math.floor(day / 7);
    if (week < 5) return `${week}w ago`;
    const month = Math.floor(day / 30);
    if (month < 12) return `${month}mo ago`;
    return `${Math.floor(day / 365)}y ago`;
  };

  const focusComposer = () => {
    requestAnimationFrame(() => composerInputRef.current?.focus());
  };

  const handleReply = (comment: CommentType) => {
    if (!currentUser) {
      onClose();
      onPromptLogin();
      return;
    }
    setReplyTarget(comment);
    focusComposer();
  };

  const handlePostComment = async () => {
    if (!currentUser) {
      onClose();
      onPromptLogin();
      return;
    }
    const trimmed = newComment.trim();
    if (!trimmed || isPostingComment) return;

    setIsPostingComment(true);
    try {
      await Promise.resolve(onAddComment(zip.id, trimmed, replyTarget?.id || null));
      setNewComment('');
      setMentionQuery('');
      setReplyTarget(null);
    } finally {
      setIsPostingComment(false);
    }
  };

  const renderCommentBranch = (comment: ThreadedComment, depth = 0): React.ReactNode => {
    const indent = Math.min(depth, 6) * 18;
    return (
      <div key={comment.id} className="space-y-3" style={{ marginLeft: `${indent}px` }}>
        <div className={`rounded-2xl border p-3 ${depth > 0 ? 'border-white/5 bg-white/[0.03]' : 'border-white/5 bg-white/5'}`}>
          <div className="flex items-start gap-3">
            <button onClick={() => { onClose(); onViewProfile(comment.user); }}>
              <img src={comment.user.avatarUrl} alt={comment.user.username} className="h-10 w-10 rounded-xl object-cover" />
            </button>
            <div className="min-w-0 flex-1">
              <div className="mb-1 flex items-center gap-2 text-sm">
                <button onClick={() => { onClose(); onViewProfile(comment.user); }} className="font-bold hover:underline">
                  {comment.user.username}
                </button>
                {comment.user.isOnline && <span className="pulsing-dot-green h-2 w-2 rounded-full" />}
                <span className="text-xs text-gray-500">{formatTimeAgo(comment.timestamp)}</span>
              </div>
              <div className="break-words text-sm leading-relaxed text-gray-200">
                {renderWithMentions(comment.text)}
              </div>
              <div className="mt-3 flex items-center gap-4 text-xs font-semibold uppercase tracking-[0.16em] text-gray-500">
                <button onClick={() => handleReply(comment)} className="transition-colors hover:text-lime-400">
                  Reply
                </button>
                {comment.children.length > 0 && (
                  <span>{comment.children.length} {comment.children.length === 1 ? 'reply' : 'replies'}</span>
                )}
                {currentUser?.id !== comment.user.id && (
                  <button
                    onClick={() => {
                      if (!currentUser) { onPromptLogin(); return; }
                      onReportComment?.(zip.id, comment.id);
                    }}
                    className="transition-colors hover:text-red-400"
                  >
                    Report
                  </button>
                )}
                {isModerator && (
                  <button
                    onClick={() => {
                      if (window.confirm('Delete this comment?')) onDeleteComment?.(zip.id, comment.id);
                    }}
                    className="text-red-400 transition-colors hover:text-red-300"
                  >
                    Delete
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
        {comment.children.length > 0 && (
          <div className="space-y-3 border-l border-white/10 pl-3">
            {comment.children.map((child) => renderCommentBranch(child, depth + 1))}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="fixed inset-0 z-[1300] text-white">
      <div
        className={`absolute inset-0 bg-black/60 transition-opacity duration-300 ${slideIn ? 'opacity-100' : 'opacity-0'}`}
        onClick={handleRequestClose}
      />
      <div
        className={`absolute inset-x-0 bottom-0 flex max-h-[88vh] flex-col rounded-t-[1.75rem] border-t border-white/10 bg-app-surface shadow-2xl transform transition-transform duration-300 ease-out ${slideIn ? 'translate-y-0' : 'translate-y-full'}`}
        style={{ paddingBottom: 'var(--sab-native, env(safe-area-inset-bottom, 0px))' }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex justify-center pt-2.5 pb-1" onClick={handleRequestClose}>
          <div className="h-1.5 w-10 rounded-full bg-white/25" />
        </div>

        <div className="flex items-center justify-between border-b border-white/10 px-4 pb-3">
          {currentUser?.id !== zip.user.id ? (
            <div className="relative">
              <button
                onClick={() => setIsOptionsMenuOpen((prev) => !prev)}
                className="flex h-10 w-10 items-center justify-center rounded-full transition-colors hover:bg-white/10"
                aria-label="Zip options"
              >
                <OvalDotsIcon className="h-5 w-5" />
              </button>
              {isOptionsMenuOpen && (
                <>
                  <div className="fixed inset-0 z-10" onClick={() => setIsOptionsMenuOpen(false)} />
                  <div className="absolute left-0 top-[calc(100%+0.5rem)] z-20 w-48 overflow-hidden rounded-lg border border-white/10 bg-black shadow-xl">
                    <button
                      className="w-full px-3 py-2.5 text-left text-sm font-semibold text-red-400 transition-colors hover:bg-white/10"
                      onClick={() => {
                        setIsOptionsMenuOpen(false);
                        if (!currentUser) { onPromptLogin(); return; }
                        onReportZip?.(zip.id);
                      }}
                    >
                      Report Post
                    </button>
                  </div>
                </>
              )}
            </div>
          ) : (
            <div className="w-10" />
          )}
          <h2 className="text-sm font-bold text-white">{comments.length} {comments.length === 1 ? 'Comment' : 'Comments'}</h2>
          <button
            onClick={handleRequestClose}
            className="flex h-10 w-10 items-center justify-center rounded-full transition-colors hover:bg-white/10"
            aria-label="Close comments"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto overscroll-contain px-4 py-4">
          {zip.caption && (
            <div className="mb-4 flex items-start gap-3">
              <button onClick={() => { onClose(); onViewProfile(zip.user); }} className="flex-shrink-0">
                <img src={zip.user.avatarUrl} alt={zip.user.username} className="h-10 w-10 rounded-xl object-cover" />
              </button>
              <div className="min-w-0 flex-1 text-sm leading-relaxed text-gray-200">
                <button onClick={() => { onClose(); onViewProfile(zip.user); }} className="mr-2 font-bold hover:underline">
                  {zip.user.username}
                </button>
                {renderWithMentions(zip.caption)}
                <div className="mt-1 text-xs text-gray-500">{formatTimeAgo(zip.timestamp)}</div>
              </div>
            </div>
          )}

          {threadedComments.length > 0 && (
            <div className="space-y-4">
              {threadedComments.map((comment) => renderCommentBranch(comment))}
            </div>
          )}
        </div>

        <div className="keyboard-avoiding-bar border-t border-white/10 bg-app-surface px-4 py-3">
          {currentUser ? (
            <div className="space-y-2">
              {replyTarget && (
                <div className="flex items-center justify-between rounded-2xl border border-lime-500/25 bg-lime-500/10 px-3 py-2 text-sm">
                  <div className="min-w-0">
                    <span className="text-gray-300">Replying to </span>
                    <span className="font-bold text-lime-300">{'@' + String(replyTarget.user.username).toUpperCase()}</span>
                  </div>
                  <button onClick={() => setReplyTarget(null)} className="flex h-8 w-8 items-center justify-center rounded-full hover:bg-white/10" aria-label="Cancel reply">
                    <XMarkIcon className="h-4 w-4" />
                  </button>
                </div>
              )}

              <div className="flex items-center gap-3">
                <img src={currentUser.avatarUrl} alt="Your avatar" className="h-10 w-10 rounded-xl object-cover" />
                <div className="relative flex-1">
                  <input
                    ref={composerInputRef}
                    type="text"
                    value={newComment}
                    onChange={(e) => {
                      const value = e.target.value;
                      setNewComment(value);
                      const match = value.match(/@([A-Za-z0-9_]+)$/);
                      setMentionQuery(match ? match[1].toLowerCase() : '');
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        handlePostComment();
                      }
                    }}
                    placeholder={replyTarget ? `Reply to @${replyTarget.user.username}...` : 'Add a comment...'}
                    className="w-full rounded-full border border-gray-700 bg-black/70 py-3 pl-4 pr-12 text-white focus:outline-none focus:ring-2 focus:ring-lime-500"
                  />
                  <button
                    onClick={handlePostComment}
                    disabled={!newComment.trim() || isPostingComment}
                    className="absolute right-1 top-1/2 -translate-y-1/2 rounded-full p-2 text-lime-400 disabled:text-gray-600"
                    aria-label={replyTarget ? 'Post reply' : 'Post comment'}
                  >
                    <PaperAirplaneIcon className="h-5 w-5" />
                  </button>
                </div>
              </div>

              {mentionQuery && (
                <div className="ml-[3.25rem] max-h-40 overflow-y-auto rounded-2xl border border-gray-800 bg-black shadow-xl">
                  {allUsers
                    .filter((user) => user.username.toLowerCase().includes(mentionQuery))
                    .slice(0, 6)
                    .map((user) => (
                      <button
                        key={user.id}
                        className="flex w-full items-center gap-3 px-3 py-2 text-left hover:bg-white/5"
                        onClick={() => {
                          const withoutQuery = newComment.replace(/@([A-Za-z0-9_]+)$/, '');
                          setNewComment(`${withoutQuery}@${user.username} `);
                          setMentionQuery('');
                          focusComposer();
                        }}
                      >
                        <img src={user.avatarUrl} alt={user.username} className="h-7 w-7 rounded-lg object-cover" />
                        <span className="text-sm text-white">{user.username}</span>
                      </button>
                    ))}
                </div>
              )}
            </div>
          ) : (
            <button onClick={() => { onClose(); onPromptLogin(); }} className="w-full rounded-2xl bg-lime-500 px-4 py-3 text-sm font-bold text-black transition hover:bg-lime-400">
              Log in to comment
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default ZipCommentsPage;
